import { Box, Tabs, TabList, Tab } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    useNavigate,
    useParams,
    useSearchParams,
    useLocation,
} from 'react-router-dom';

import FormBase from 'components/forms/new/FormBase';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';
import useFieldValidation from 'hooks/useFieldValidation';
import DialPlansDropdown from './components/DialPlansDropdown';
import RulesTable from './components/RulesTable';
import { formContainerStyles, tabContentStyles } from './constants';
import DialPlanNameField from './formFields/DialPlanNameField';
import { useGetDialPlan, useUpdateDialPlan } from './queries';
import { dialPlanSchema } from './schema';
import { customTabStyles, selectedTabStyles } from '../../../../constants';

const DialPlanEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { dialPlanId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { darkMode } = useSelector((state) => state.settings);
    const { data: dialPlan, isLoading } = useGetDialPlan(dialPlanId);
    const { mutate: updateDialPlan, isLoading: isUpdating } =
        useUpdateDialPlan();
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ name: '' });

    useEffect(() => {
        if (dialPlan) {
            setModalState({ name: dialPlan.name });
        }
    }, [dialPlan]);

    const breadCrumbTrail = [
        {
            label: 'Dial Plans',
            path: '/dial-plans',
        },
        {
            label: <DialPlansDropdown currentId={dialPlanId} />,
            path: undefined,
        },
    ];

    const { validatefn } = useFieldValidation(modalState, dialPlanSchema);

    const handleSubmit = (values, actions) => {
        if (!validatefn()) {
            actions.setSubmitting(false);
            return;
        }

        setError(null);
        updateDialPlan(
            { ...dialPlan, ...values },
            {
                onSuccess: () => {
                    navigate(`/dial-plans/${dialPlanId}${location.search}`);
                },
                onError: (err) => {
                    setError(
                        err?.response?.data?.message || 'An error occurred.',
                    );
                    actions.setSubmitting(false);
                },
            },
        );
    };

    if (isLoading || !dialPlan) {
        return <LoadingIndicator />;
    }

    const tabParam = searchParams.get('tab') || 'dial-plans-details';
    const tabIndex = tabParam === 'rules' ? 1 : 0;

    const handleTabChange = (index) => {
        setSearchParams({ tab: index === 1 ? 'rules' : 'dial-plans-details' });
    };

    const formBaseStyles = {
        ...tabContentStyles,
        ...(tabIndex === 0 ? formContainerStyles.noHorizontalDivider : {}),
    };
    return (
        <Box
            sx={formBaseStyles}
            className={tabIndex === 1 ? 'is-rules-tab' : ''}>
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                initialValues={{ name: dialPlan.name }}
                enableReinitialize
                finalFormSubmit={handleSubmit}
                backLink="/dial-plans"
                isLoading={isUpdating}
                tabMode={false}
                subTitleHeader
                subTitleHeaderComponent={
                    <Tabs
                        index={tabIndex}
                        onChange={handleTabChange}
                        variant="unstyled">
                        <TabList borderBottom={0} mb="10px">
                            <Tab
                                sx={customTabStyles}
                                _selected={selectedTabStyles}>
                                Dial Plans Details
                            </Tab>
                            <Tab
                                sx={customTabStyles}
                                _selected={selectedTabStyles}>
                                Rules
                            </Tab>
                        </TabList>
                    </Tabs>
                }>
                {(formProps) => {
                    const { errors, touched, handleChange } = formProps;
                    if (tabIndex === 1) {
                        return <RulesTable dialPlanId={dialPlanId} />;
                    }
                    return [
                        <DialPlanNameField
                            key="name"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            isEdit
                            onChange={(e) => {
                                handleChange(e);
                                setModalState((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }));
                            }}
                        />,
                        error && (
                            <Box key="error-box" paddingBottom="0.5rem">
                                <Alert
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ];
                }}
            </FormBase>
        </Box>
    );
};

export default DialPlanEdit;
